import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactApexChart from "react-apexcharts";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import IntegrationImage from "../../../../stack/integrations/components/IntegrationImage";
import WidgetWrapper from "../../WidgetWrapper";
import {
  baseTimePointOptions,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";

const ToolCoverageWidget = forwardRef(({ mainTimePoint }, ref) => {
  const { getToolsCoverageTrend, trackEvent } = useAPI();
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
  const colors = useColors();
  const widgetContentRef = useRef(null);

  const colorsChart = [
    colors.primary_200,
    colors.primary_100,
    colors.primary_50,
    colors.primary_25,
    colors.secondary_100,
    colors.secondary_200,
    colors.secondary_300,
    colors.secondary_400,
  ];

  const colorsChart48percent = [
    colors.primary_200_48percent,
    colors.primary_100_48percent,
    colors.primary_50_48percent,
    colors.primary_25_48percent,
    colors.secondary_100_48percent,
    colors.secondary_200_48percent,
    colors.secondary_300_48percent,
    colors.secondary_400_48percent,
  ];

  const {
    data: toolsCoverageTrend,
    isLoading: isLoadingToolsPerformanceTrend,
  } = useQuery({
    queryKey: dashboardKeys.widgetType("toolsCoverageTrend"),
    queryFn: getToolsCoverageTrend,
    select: (data) => data.data,
  });

  useEffect(() => {
    if (toolsCoverageTrend && toolsCoverageTrend[mainTimePoint]) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint]);

  // Sort menu items for the dropdown filter
  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    toolsCoverageTrend,
    icons,
    trackEvent,
    "tool-coverage"
  );

  // All tool coverage data with current and base coverage
  const allToolCoverageData = useMemo(() => {
    if (!toolsCoverageTrend || Object.keys(toolsCoverageTrend).length === 0) {
      return [];
    }

    const currentData = toolsCoverageTrend[LATEST];
    const baseData =
      baseTimePoint === LATEST
        ? currentData
        : toolsCoverageTrend[baseTimePoint];

    return Object.entries(currentData)
      .map(([toolName, currentToolData]) => {
        const baseToolData = baseData[toolName] || {
          all_entities: 0,
          weightedEntityCount: 0,
          totalSeverityWeight: 0,
        };

        const currentCoverage =
          currentToolData.all_entities > 0
            ? Math.round(
                (currentToolData.weightedEntityCount /
                  (currentToolData.all_entities *
                    currentToolData.totalSeverityWeight)) *
                  100
              )
            : 0;

        const baseCoverage =
          baseToolData.all_entities > 0
            ? Math.round(
                (baseToolData.weightedEntityCount /
                  (baseToolData.all_entities *
                    baseToolData.totalSeverityWeight)) *
                  100
              )
            : 0;

        const coverageChange = currentCoverage - baseCoverage;
        const percentageChange = Math.abs(coverageChange);
        const isPositive = coverageChange >= 0;

        return {
          tool: toolName,
          displayName:
            IntegrationsMapping[toolName]?.shortDisplayName ||
            IntegrationsMapping[toolName]?.displayName ||
            toolName,
          currentCoverage: currentCoverage,
          baseCoverage: baseCoverage,
          coverageChange: coverageChange,
          percentageChange: percentageChange,
          isPositive: isPositive,
          resolved: currentToolData.weightedEntityCount,
        };
      })
      .filter((tool) => tool.resolved > 0) // Only show tools with controls
      .sort((a, b) => b.currentCoverage - a.currentCoverage);
  }, [toolsCoverageTrend, baseTimePoint]);

  // Get top 8 tools by default
  const topTools = useMemo(
    () => allToolCoverageData.slice(0, 8).map((tool) => tool.tool),
    [allToolCoverageData]
  );

  // Initialize selected tools as top 8 tools
  const [selectedTools, setSelectedTools] = useState([]);

  // Update selected tools when top tools change (only if currently empty)
  useEffect(() => {
    if (selectedTools.length === 0 && topTools.length > 0) {
      setSelectedTools(topTools);
    }
  }, [topTools, selectedTools.length]);

  // Filter data based on selected tools
  const toolCoverageData = useMemo(
    () =>
      allToolCoverageData.filter((tool) => selectedTools.includes(tool.tool)),
    [allToolCoverageData, selectedTools]
  );

  const MAX_TOOLS_SELECTED = 8;

  // Selected tools to display in the dropdown
  const selectedToolsToDisplay = {
    title: "Select Tools",
    key: "selected-tools",
    disabled: false,
    onclick: () => {},
    trailingIcon: icons.chevronRightIcon,
    submenuTitle: "Select Tools (Up to 8)",
    submenuMultiple: true,
    submenu: allToolCoverageData.map((option) => ({
      title: (
        <Box
          display={"flex"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={1}
        >
          <IntegrationImage
            name={option.tool?.toLowerCase()}
            width={"12px"}
            newLogo={true}
          />
          <Typography variant="caption">{option.displayName}</Typography>
        </Box>
      ),
      key: option.tool,
      selected: selectedTools.includes(option.tool),
      onclick: () => {
        if (selectedTools.includes(option.tool)) {
          setSelectedTools((prev) =>
            prev.filter((tool) => tool !== option.tool)
          );
        } else {
          if (selectedTools.length < MAX_TOOLS_SELECTED) {
            setSelectedTools((prev) => [...prev, option.tool]);
          }
        }
      },
    })),
  };

  const apexSeries = useMemo(
    () => [
      {
        name:
          baseTimePoint === LATEST
            ? "Current"
            : baseTimePointOptions[baseTimePoint],
        data: toolCoverageData.map((tool, index) => ({
          x: tool.displayName,
          y: tool.baseCoverage,
          fillColor: colorsChart48percent[index % colorsChart48percent.length],
        })),
      },
      {
        name: "Current",
        data: toolCoverageData.map((tool, index) => ({
          x: tool.displayName,
          y: tool.currentCoverage,
          fillColor: colorsChart[index % colorsChart.length],
        })),
      },
    ],
    [toolCoverageData, baseTimePoint, colorsChart]
  );

  const apexOptions = useMemo(() => {
    return {
      chart: {
        type: "bar",
        height: 350,
        toolbar: { show: false },
        fontFamily: "inherit",
        // Force re-render when data changes
        id: `tool-coverage-${toolCoverageData.length}-${baseTimePoint}`,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "32px",
          borderRadius: 4,
          borderRadiusApplication: "end",
          rangeBarOverlap: false,
        },
      },
      colors: apexSeries.flatMap((s) => s.data.map((d) => d.fillColor)),
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function ({ dataPointIndex }) {
          const tool = toolCoverageData[dataPointIndex];
          if (!tool) return "";

          const current = tool.currentCoverage;
          const previous = tool.baseCoverage;
          const trend = tool.percentageChange;
          const isPositive = tool.isPositive;

          const arrow = isPositive ? "↑" : trend === 0 ? "" : "↓";
          const trendColor = isPositive
            ? colors.success_main
            : trend === 0
            ? colors.text_disabled
            : colors.error_main;
          const trendPrefix = isPositive ? "+" : "";

          return `
          <div style="
            font-family: inherit;
            text-align: left;
            transform: translateX(-50%);
            position: relative;
            left: 50%;
            background: white;
            padding: 12px;
            border-radius: 4px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
            min-width: 140px;
          ">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <div style="font-size: 14px; color: #212B36;">
                ${tool.displayName}
              </div>
              ${
                baseTimePoint !== LATEST
                  ? `
                <div style="font-size: 14px; color: ${trendColor}; margin-left: 8px;">
                  ${arrow}<span style="margin-left: 4px;">${trendPrefix}${trend}%</span>
                </div>
              `
                  : ""
              }
            </div>
            <div style="font-size: 12px; margin-bottom: 4px;">
              <span style="color: #454F5B;">Current:</span>
              <span style="color: #212B36; margin-left: 4px;">${current}%</span>
            </div>
          
            ${
              baseTimePoint !== LATEST
                ? `
              <div style="font-size: 12px;">
                <span style="color: #454F5B;">Previous:</span>
                <span style="color: #212B36; margin-left: 4px;">${previous}%</span>
              </div>
            `
                : ""
            }
          </div>
        `;
        },
      },
      grid: {
        strokeDashArray: 4,
        borderColor: "rgba(145, 158, 171, 0.2)",
      },
      xaxis: {
        categories: toolCoverageData.map((tool) => tool.displayName),
        labels: {
          style: {
            colors: colors.text_disabled,
            fontSize: "10px",
          },
        },
      },
      yaxis: {
        max: 100,
        min: 0,
        labels: {
          style: {
            colors: colors.text_disabled,
            fontSize: "10px",
          },
          formatter: (value) => `${value}%`,
        },
      },
      legend: {
        show: false,
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    };
  }, [
    toolCoverageData,
    baseTimePoint,
    apexSeries,
    colors.error_main,
    colors.success_main,
    colors.text_disabled,
  ]);

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingToolsPerformanceTrend)
        return null;

      if (!isCombinedExport) {
        trackEvent("User clicked on Export to PDF button in Tool Coverage");
      }

      try {
        if (isCombinedExport) {
          // For combined export, return just the widget content with header
          const widgetContainer = document.createElement("div");

          // Add widget header
          const widgetHeader = createPdfHeader("Tool Coverage Report");
          widgetContainer.appendChild(widgetHeader);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

          // Enhance chart visibility for PDF
          const chartElement = widgetClone.querySelector(".apexcharts-canvas");
          if (chartElement) {
            chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
          }

          widgetContainer.appendChild(widgetClone);

          return widgetContainer;
        } else {
          // Individual export - original functionality
          const entirePageElement = await createPdfContainer();

          const header = createPdfHeader("Tool Coverage Report");
          entirePageElement.appendChild(header);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 89%;
          min-height: fit-content;
        `;

          // Enhance chart visibility for PDF
          const chartElement = widgetClone.querySelector(".apexcharts-canvas");
          if (chartElement) {
            chartElement.style.cssText = `
            width: 89% !important;
            height: 350px !important;
          `;
          }

          entirePageElement.appendChild(widgetClone);

          // Measure content dimensions
          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          // Generate PDF
          const pdfConfig = createPdfConfig(
            generateFileName("Breeze_Tool_Coverage", null, FILE_EXTENSIONS.PDF),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        Toast.error("Failed to export Tool Coverage, Please try again later");
        return null;
      }
    },
    [widgetContentRef, isLoadingToolsPerformanceTrend]
  );

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "toolCoverage",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  return (
    <WidgetWrapper
      title={"Tool Coverage"}
      sortMenuItems={{
        data: [...sortMenuItems, selectedToolsToDisplay],
        tooltip: "View changes since selected time",
      }}
      info="Shows current coverage of affected entities per tool."
      viewAll={{
        handleViewAll: () => {
          window.open("/posture/controls", "_blank");
        },
        tooltip: "View all tools performance data",
        newTab: true,
      }}
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      {isLoadingToolsPerformanceTrend ? (
        <LoadingIcon />
      ) : Object.keys(toolsCoverageTrend).length === 0 ? (
        <Box>
          <Typography variant="caption" marginLeft={2.5}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box ref={widgetContentRef}>
          <Typography variant="caption" marginLeft={2.5}>
            Current coverage{" "}
            {baseTimePoint !== LATEST
              ? `vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""}
          </Typography>

          <Box
            sx={{ height: "350px", width: "100%", mt: 2, position: "relative" }}
          >
            <ReactApexChart
              key={`tool-coverage-chart-${toolCoverageData.length}-${baseTimePoint}`}
              options={apexOptions}
              series={baseTimePoint === LATEST ? [apexSeries[1]] : apexSeries}
              type="bar"
              height={350}
              yAxis={[
                {
                  max: 100,
                  min: 0,
                  tickLabelStyle: {
                    fontSize: "10px",
                  },
                  valueFormatter: (value) => `${value}%`,
                },
              ]}
              margin={{
                top: 20,
                right: 20,
                bottom: 40,
                left: 40,
              }}
              sx={{
                "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
                  fill: colors.text_secondary,
                },
              }}
            />

            {/* Custom Trend Labels Overlay */}
            {baseTimePoint !== LATEST && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "70px",
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                {toolCoverageData.map((tool, index) => {
                  // Calculate position for each label
                  const chartAreaWidth = 100; // Percentage width of chart area
                  const toolCount = toolCoverageData.length;
                  const barGroupWidth = chartAreaWidth / toolCount;
                  const leftPosition =
                    index * barGroupWidth + barGroupWidth / 2;

                  // Calculate vertical position based on bar height
                  const maxHeight = 350 - 80; // Chart height minus margins (40px top + 40px bottom)
                  const barHeight = (tool.currentCoverage / 100) * maxHeight;
                  const topPosition = 30 + (maxHeight - barHeight) - 20; // 40px margin + calculated position - 20px above bar

                  const trend = tool.percentageChange;
                  const trendText =
                    trend === 0
                      ? "-"
                      : `${tool.isPositive ? "+" : "-"}${trend}%`;

                  return (
                    <Box
                      key={tool.tool + index}
                      sx={{
                        position: "absolute",
                        left: `${leftPosition}%`,
                        top: `${topPosition}px`,
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "12px",
                          color: colors.text_primary,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {trendText}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

ToolCoverageWidget.displayName = "ToolCoverageWidget";

export default ToolCoverageWidget;
